'use client'

import { type PropsWithChildren, useCallback, useMemo, useRef } from 'react'
import { useMeasure } from 'react-use'
import { cn } from '../utils/styles'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip'

interface TruncatedTextProps {
  as?: 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  className?: string
  forceTooltip?: boolean
  stringLimit?: number
  children?: string
}

/**
 * This component is used to truncate text to a single line.
 * It uses the `useMeasure` hook to measure the width of the text.
 * If the text is overflowing, it will display a tooltip with the full text.
 */
export const TruncatedText = (props: PropsWithChildren<TruncatedTextProps>) => {
  const { as, children, className, forceTooltip, stringLimit, ...rest } = props

  const Component = as ? as : 'span'

  const displayNameRef = useRef<HTMLElement>(null)
  const [setMeasureRef, { width }] = useMeasure()

  const isDisplayNameOverflowing = useMemo(() => {
    if (displayNameRef.current) {
      const element = displayNameRef.current
      const isOverflowing = element.scrollWidth > Math.round(width)

      return isOverflowing
    }

    return false
  }, [width])

  const setRef = useCallback(
    (ref: HTMLElement | null) => {
      if (ref) {
        setMeasureRef(ref)
        displayNameRef.current = ref
      }
    },
    [setMeasureRef],
  )

  if (
    isDisplayNameOverflowing ||
    (forceTooltip && stringLimit && children && children.length > stringLimit)
  ) {
    return (
      <TooltipProvider>
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>
            <Component
              ref={setRef}
              className={cn(
                'whitespace-nowrap overflow-hidden text-ellipsis',
                className,
              )}
              {...rest}
            >
              {`${children?.substring(0, stringLimit)}...`}
            </Component>
          </TooltipTrigger>
          <TooltipContent hideArrow className='max-w-64'>
            <p className='text-xs font-semibold leading-4'>{children}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )
  }

  return (
    <Component
      ref={setRef}
      className={cn(
        'whitespace-nowrap overflow-hidden text-ellipsis',
        className,
      )}
      {...rest}
    >
      {children}
    </Component>
  )
}
